import { Component, ElementRef, EventEmitter, HostBinding, inject, Input, Output } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { NavigationService } from '../../../services/navigation.service';
import { SolIconComponent } from '../../core/sol-icon/sol-icon.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { NavigationTarget } from '../../../tools/router-tools';
import { NgTemplateOutlet } from '@angular/common';
import { SolNavbarItem, SolRouteId } from '../../../models/sol-navigation';
import { SolNavbarItemState } from './sol-navbar-item.state';
import { SolMenuGroup } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { SolRectangle } from '../../../services/popover.service';

export interface SolNavbarExpandedEvent {
  id: SolRouteId;
  expanded: boolean;
}

export interface SolNavbarHoverPreviewEvent {
  id: SolRouteId;
  menu: SolMenuGroup[];
  bounds: SolRectangle;
  boundsRef: ElementRef<HTMLElement>;
}

@Component({
  selector: 'sol-navbar-item',
  imports: [SolIconComponent, TranslatePipe, NgTemplateOutlet],
  templateUrl: './sol-navbar-item.component.html',
})
export class SolNavbarItemComponent extends AbstractComponent<SolNavbarItemState> {
  // Dependencies
  protected readonly navigationService = inject(NavigationService);

  // Computed
  protected readonly text$ = this.compute(state => state.text);
  protected readonly prependIcon$ = this.compute(state => state.prependIcon);
  protected readonly appendIcon$ = this.compute(state => state.appendIcon);
  protected readonly isMenuGroup$ = this.compute(state => state.isMenuGroup);
  protected readonly isTopLevelGroup$ = this.compute(state => state.isTopLevelGroup);
  protected readonly expanded$ = this.compute(state => state.expanded);
  protected readonly expandedIds$ = this.compute(state => state.expandedIds);
  protected readonly children$ = this.compute(state => state.children);
  protected readonly level$ = this.compute(state => state.level);
  protected readonly buttonClasses$ = this.compute(state => state.buttonClasses);
  protected readonly activeRouteIds$ = this.compute(state => state.activeRouteIds);

  // Events
  @Output('expanded')
  private readonly expandedEmitter = new EventEmitter<SolNavbarExpandedEvent>();
  @Output('hoverPreview')
  private readonly hoverPreviewEmitter = new EventEmitter<SolNavbarHoverPreviewEvent>();
  @Output('hoverPreviewClose')
  private readonly hoverPreviewCloseEmitter = new EventEmitter<void>();

  constructor() {
    super(SolNavbarItemState.of());
  }

  @HostBinding('class')
  public get hostClass(): string {
    return 'sol-navbar-item';
  }

  @Input()
  public set item(item: SolNavbarItem) {
    this.update(state => state.withItem(item));
  }

  @Input()
  public set activeRouteIds(activeRouteIds: SolRouteId[]) {
    this.update(state => state.withActiveRouteIds(activeRouteIds));
  }

  @Input()
  public set expandedIds(expandedIds: SolRouteId[]) {
    this.update(state => state.withExpandedIds(expandedIds));
  }

  @Input()
  public set level(level: number) {
    this.update(state => state.withLevel(level));
  }

  protected onClick(event: MouseEvent, newTab: boolean = false): void {
    event.stopPropagation();

    const state = this.state;
    if (state.selected) {
      return;
    } else if (state.item.isMenuGroup) {
      this.hoverPreviewCloseEmitter.emit();
      const expanded = !state.expandedIds.includes(state.item.id);
      this.expandedEmitter.emit({
        id: state.item.id,
        expanded,
      });
    } else {
      this.navigationService.navigate(state.item.id, {
        target: newTab ? NavigationTarget.NewTab : NavigationTarget.SameTab,
      });
    }
  }

  protected onHoverPreview(event: MouseEvent): void {
    const state = this.state;
    if (!state.hoverPreviewEnabled) {
      return;
    }
    this.hoverPreviewEmitter.emit({
      id: state.item.id,
      menu: state.hoverPreviewMenu,
      bounds: this.toRightBounds(event),
      boundsRef: new ElementRef(event.currentTarget as HTMLElement),
    });
  }

  protected onExpanded(event: SolNavbarExpandedEvent) {
    this.expandedEmitter.emit(event);
  }

  protected onHoverPreviewFromChild(event: SolNavbarHoverPreviewEvent): void {
    this.hoverPreviewEmitter.emit(event);
  }

  protected onHoverPreviewClose(): void {
    this.hoverPreviewCloseEmitter.emit();
  }

  private toRightBounds(event: MouseEvent): SolRectangle {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    return {
      x: rect.x + rect.width,
      y: rect.y,
      width: 0,
      height: 0,
    };
  }
}
